import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { ContactService } from '../../services/contact.service';
import { LegalComponent } from '../../dialogs/legal/legal.component';
import { TranslocoService } from '@jsverse/transloco';
import { LanguageService } from '../../i18n/language.service';

@Component({
  selector: 'app-contact',
  standalone: false,
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactComponent {
  readonly maxMessageLength = 10000;
  form: FormGroup;
  submitting = false;
  successMessage: string | null = null;
  errorMessage: string | null = null;

  constructor(
    private fb: FormBuilder,
    private contactService: ContactService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private transloco: TranslocoService,
    public language: LanguageService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(200)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(254)]],
      message: ['', [Validators.required, Validators.maxLength(this.maxMessageLength)]],
      // Honeypot: hidden in UI; bots that autofill every field get rejected server-side.
      website: ['']
    });
  }

  get messageLength(): number {
    return (this.form.get('message')?.value || '').length;
  }

  openLegal(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    this.dialog.open(LegalComponent, {
      maxWidth: '650px'
    });
  }

  onSubmit(): void {
    this.successMessage = null;
    this.errorMessage = null;
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    this.submitting = true;
    this.cdr.markForCheck();

    const { name, email, message, website } = this.form.value;
    this.contactService.send({
      name: (name || '').trim(),
      email: (email || '').trim(),
      message: (message || '').trim(),
      website: (website || '').trim()
    }).subscribe({
      next: (res) => {
        this.submitting = false;
        this.successMessage = res.message || this.transloco.translate('contact.successDefault');
        this.form.reset();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        const body = err.error;
        this.errorMessage =
          (body && typeof body === 'object' && body.error) ||
          this.transloco.translate('contact.errorSendFailed');
        this.cdr.markForCheck();
      }
    });
  }
}
